import pLimit from "p-limit";
import api from "../api";

/**
 * Direct-to-R2 multipart uploader for file transfers.
 *
 * - The API creates the multipart upload and signs part URLs; bytes go straight from the
 *   browser to Cloudflare R2 (no server hop).
 * - Parts of every file share one pool of PART_CONCURRENCY parallel PUTs, so a few big videos
 *   and many small JPEGs both saturate the connection.
 * - A failed part is retried with backoff; when the browser goes offline it waits for the
 *   connection to come back and continues from the missing parts only.
 *
 * Requires the R2 bucket CORS to allow PUT from the app origin and expose the ETag header.
 */

const PART_CONCURRENCY = 6;
const FILE_INIT_CONCURRENCY = 4;
const SIGN_BATCH = 20;
const MAX_RETRIES = 5;

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const waitOnline = () =>
  navigator.onLine
    ? Promise.resolve()
    : new Promise((resolve) => window.addEventListener("online", resolve, { once: true }));

class UploadCancelled extends Error {}

function putPart(url, blob, onProgress, signal) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    xhr.upload.onprogress = (e) => onProgress(e.loaded);
    xhr.onload = () => {
      const etag = xhr.getResponseHeader("ETag");
      if (xhr.status >= 200 && xhr.status < 300 && etag) resolve(etag);
      else reject(new Error(`Part upload failed (${xhr.status})`));
    };
    xhr.onerror = () => reject(new Error("Network error"));
    xhr.onabort = () => reject(new UploadCancelled());
    signal.addEventListener("abort", () => xhr.abort(), { once: true });
    xhr.send(blob);
  });
}

/**
 * @param {object}   opts
 * @param {string}   opts.base         "/transfers/{id}" or "/transfers/public/{token}"
 * @param {File[]}   opts.files
 * @param {object}   [opts.extra]      extra fields sent when a file is initialised (uploader_name / uploader_email)
 * @param {function} opts.onFileUpdate (index, {status, uploaded, fileId, error}) — status: queued|uploading|done|error
 * @returns {{promise: Promise<{fileIds: number[], bytes: number}>, cancel: function}} resolves with what was uploaded
 */
export function startTransferUpload({ base, files, extra = {}, onFileUpdate }) {
  const controller = new AbortController();
  const partLimit = pLimit(PART_CONCURRENCY);
  const initLimit = pLimit(FILE_INIT_CONCURRENCY);
  const doneIds = [];
  let doneBytes = 0;

  const uploadOne = async (file, index) => {
    const partLoaded = {};
    const report = (patch) =>
      onFileUpdate(index, {
        uploaded: Object.values(partLoaded).reduce((a, b) => a + b, 0),
        ...patch,
      });

    const { data: init } = await initLimit(() =>
      api.post(`${base}/files`, { name: file.name, size: file.size, mime: file.type || null, ...extra })
    );
    const { file_id: fileId, part_size: partSize } = init;
    const fileBase = `${base}/files/${fileId}`;
    report({ status: "uploading", fileId });

    const partCount = Math.max(1, Math.ceil(file.size / partSize));
    const partNumbers = Array.from({ length: partCount }, (_, i) => i + 1);
    const urls = {};
    const sign = async (numbers) => {
      const { data } = await api.post(`${fileBase}/parts`, { part_numbers: numbers });
      Object.assign(urls, data.urls);
    };

    const uploadPart = async (n) => {
      const blob = file.slice((n - 1) * partSize, Math.min(n * partSize, file.size));
      for (let attempt = 0; ; attempt++) {
        if (controller.signal.aborted) throw new UploadCancelled();
        try {
          if (!urls[n]) {
            const batch = partNumbers.slice(n - 1, n - 1 + SIGN_BATCH).filter((p) => !urls[p]);
            await sign(batch);
          }
          const etag = await putPart(
            urls[n],
            blob,
            (loaded) => {
              partLoaded[n] = loaded;
              report({ status: "uploading" });
            },
            controller.signal
          );
          partLoaded[n] = blob.size;
          return { PartNumber: n, ETag: etag };
        } catch (err) {
          if (err instanceof UploadCancelled || attempt >= MAX_RETRIES) throw err;
          partLoaded[n] = 0;
          delete urls[n]; // the signed URL may have expired: sign again
          await waitOnline();
          await sleep(Math.min(1000 * 2 ** attempt, 15000));
        }
      }
    };

    try {
      const parts = await Promise.all(partNumbers.map((n) => partLimit(() => uploadPart(n))));
      await api.post(`${fileBase}/complete`, { parts });
      doneIds.push(fileId);
      doneBytes += file.size;
      report({ status: "done" });
    } catch (err) {
      api.post(`${fileBase}/abort`).catch(() => {});
      throw err;
    }
  };

  const promise = Promise.all(
    files.map((file, index) =>
      uploadOne(file, index).catch((err) => {
        if (err instanceof UploadCancelled || controller.signal.aborted) return;
        onFileUpdate(index, {
          status: "error",
          error: err?.response?.data?.detail || err.message,
        });
      })
    )
  ).then(() => ({ fileIds: doneIds, bytes: doneBytes }));

  return { promise, cancel: () => controller.abort() };
}
